import { useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchServiceProducts } from "state/prestationService/actions";
import {
  addOrderItem,
  removeOrderItem,
  updateOrderItem,
  setOrderServiceInfo,
  setOrderBriqueDevis,
} from "state/orderEdition/actions";
import {
  calculateServiceHoursSplit,
  calculateWaiterCount,
} from "helper/serviceCalculation";
import { resolveTenderUnitPrice } from "utils/tender/tenderCatalogUtils";

const isTruthy = (v) => v === true || v === 1 || v === "1";
const toInt = (v, fallback = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Fallback : nom historique de la brique cible si aucun template avec
 * `has_service_option = true` n'est trouvé en store (env. pas encore migré
 * ou liste briques pas chargée).
 */
const SERVICE_BRIQUE_FALLBACK_NAME = "Service et Logistique";

/**
 * Hook qui :
 *   1. Charge la liste des produits service (tag `service`) au montage.
 *   2. Initialise `serviceInfo.masterCount` / `waiterCount` à la coche.
 *   3. Recalcule `waiterCount` quand `deliveryInfo.person` change.
 *   4. **Expose des helpers** (`createServiceLines`, `removeAllServiceLines`,
 *      `syncServiceLinesToCounts`, `updateServiceLine`, `removeServiceLine`)
 *      pour que le composant manipule les lignes service comme des articles
 *      classiques, **via ses event handlers** (pas via useEffect).
 *
 * **Aucun useEffect ne touche aux items**. Cette discipline évite les
 * cascades de re-renders observées dans les tentatives précédentes
 * (cf. discussion 2026-05-22 — boucles infinies déclenchées par des
 * useEffect qui dispatchaient sur changement de `order.items` ou
 * `order.briqueDevis`).
 *
 * Mode brique vs classique : les helpers forkent en interne.
 *  - briqMode=true  → 1 seul `setOrderBriqueDevis` par opération (atomique)
 *  - briqMode=false → addOrderItem / removeOrderItem / updateOrderItem
 *
 * À utiliser dans `ServiceInfoComponent` :
 *   const sync = useServiceLineSync(orderIndex);
 *   // dans updateServiceFlag(true) : sync.createServiceLines(1, N);
 *   // dans updateServiceFlag(false) : sync.removeAllServiceLines();
 *   // dans onChange counts : sync.syncServiceLinesToCounts();
 */
export const useServiceLineSync = (orderIndex) => {
  const dispatch = useDispatch();
  const order = useSelector((s) => s.orderEdition.orders?.[orderIndex]);
  const serviceState = useSelector((s) => s.prestationService);
  const previousFlag = useRef(undefined);

  // Fetch des produits service au premier montage uniquement.
  // NB : l'ancienne version retentait si `isInitialized && list.length === 0`
  // ("staleEmpty re-fetch") — mais ce cas se produit légitimement quand l'API
  // renvoie une liste vide (env. fraîche où le tag `service` n'a pas encore
  // été seedé via `app:tag-prestation-service`). Le re-fetch déclenchait alors
  // une boucle infinie (COMPLETED [] → staleEmpty=true → re-dispatch → repeat).
  // Si la liste est vraiment vide, l'utilisateur recharge la page.
  useEffect(() => {
    if (serviceState.isInitialized) return;
    if (serviceState.loading) return;
    dispatch(fetchServiceProducts());
  }, [serviceState.isInitialized, serviceState.loading, dispatch]);

  const serviceEnabled = isTruthy(order?.deliveryInfo?.service);
  const personValue = order?.deliveryInfo?.person;

  // ---------------------------------------------------------------------------
  // Effet 1 — Init des compteurs à la coche off→on (touche UNIQUEMENT serviceInfo).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!serviceState.isInitialized) return;
    const prev = previousFlag.current;
    previousFlag.current = serviceEnabled;
    if (prev === undefined) return;
    if (serviceEnabled === prev) return;
    if (!serviceEnabled) return; // décoche : géré par updateServiceFlag dans le composant

    const currentMaster = toInt(order?.serviceInfo?.masterCount, 0);
    const currentWaiter = toInt(order?.serviceInfo?.waiterCount, 0);
    if (currentMaster !== 0 && currentWaiter !== 0) return;

    dispatch(setOrderServiceInfo(orderIndex, {
      ...(order?.serviceInfo || {}),
      masterCount: currentMaster === 0 ? 1 : currentMaster,
      waiterCount: currentWaiter === 0 ? calculateWaiterCount(personValue) : currentWaiter,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceEnabled, serviceState.isInitialized]);

  // ---------------------------------------------------------------------------
  // (Anciennement Effet 2 — recalcul auto de waiterCount sur changement de
  // `person`.) SUPPRIMÉ : le nombre de serveurs (et de maîtres d'hôtel) ne
  // change plus en temps réel. Le recalcul est déclenché manuellement via le
  // bouton "Actualiser le calcul" (cf. BriqueServiceSection / ServiceInfoComponent),
  // qui recompute waiterCount = calculateWaiterCount(person) puis reconstruit
  // les lignes. L'indicateur de désync compare waiterCount à la formule pour
  // signaler quand le bouton doit être actionné.
  // ---------------------------------------------------------------------------

  // ===========================================================================
  // HELPERS — appelés par les event handlers du composant. AUCUN useEffect
  // ne les déclenche → pas de cascade possible.
  // ===========================================================================

  const briqMode = !!order?.briqMode;
  const briqueTemplates = useSelector((s) => s.adminData?.briques?.data || []);
  // Catalogue de l'appel d'offres sélectionné (null hors AO) : sert à poser le
  // tarif horaire négocié sur les lignes service au lieu du tarif standard.
  const tenderCatalog = useSelector((s) => s.tender?.tenderCatalog?.data || null);

  /**
   * Index dans `order.briqueDevis` de la brique cible pour les lignes service.
   * Détection :
   *  1. Match par nom contre un BriqueTemplate avec `has_service_option = true`
   *     (= la source de vérité côté Sonata Admin).
   *  2. Fallback : brique nommée `SERVICE_BRIQUE_FALLBACK_NAME` (historique).
   *  3. Retourne -1 si rien trouvé.
   *
   * Si plusieurs briques du devis matchent un template `has_service_option`,
   * on retourne le premier (la validation cross-brique est faite en amont
   * dans `findServiceOptionConflicts()` et bloque la sauvegarde).
   */
  const serviceBlockIndex = useMemo(() => {
    if (!briqMode) return -1;
    const briques = order?.briqueDevis || [];
    if (briques.length === 0) return -1;

    const optionEnabledNames = new Set(
      (briqueTemplates || [])
        .filter((t) => t?.has_service_option === true)
        .map((t) => t?.name)
        .filter(Boolean)
    );

    if (optionEnabledNames.size > 0) {
      const idx = briques.findIndex((b) => optionEnabledNames.has(b?.name));
      if (idx !== -1) return idx;
    }
    return briques.findIndex((b) => b?.name === SERVICE_BRIQUE_FALLBACK_NAME);
  }, [briqMode, order?.briqueDevis, briqueTemplates]);

  /**
   * Détecte les conflits cross-brique : si > 1 brique du devis porte un
   * template avec `has_service_option = true`, la sauvegarde doit être
   * bloquée (le calcul service ne sait pas où injecter les lignes).
   *
   * Retourne la liste des noms en conflit (vide si OK).
   */
  const findServiceOptionConflicts = useCallback(() => {
    if (!briqMode) return [];
    const briques = order?.briqueDevis || [];
    const optionEnabledNames = new Set(
      (briqueTemplates || [])
        .filter((t) => t?.has_service_option === true)
        .map((t) => t?.name)
        .filter(Boolean)
    );
    if (optionEnabledNames.size === 0) return [];
    const matched = briques.filter((b) => optionEnabledNames.has(b?.name));
    return matched.length > 1 ? matched.map((b) => b.name) : [];
  }, [briqMode, order?.briqueDevis, briqueTemplates]);

  /**
   * Prix unitaire d'un produit service pour la prestation courante.
   *
   * Quand la commande est rattachée à un appel d'offres, le tarif horaire
   * négocié au marché prime sur le tarif standard du catalogue (ex. Ville de
   * Lyon Lot 2 : serveur à 33,50 € au lieu de 37,80 €). Le nom de l'item AO
   * est repris comme `renammed`, comme pour un produit ajouté à la main.
   */
  const tenderPriceFor = useCallback((product) => {
    const resolved = resolveTenderUnitPrice(
      tenderCatalog,
      { productId: product.id },
      {
        person: toInt(order?.deliveryInfo?.person, 1) || 1,
        prestationType: order?.prestationType || null,
        fallbackPrice: product.price,
      }
    );
    return {
      price: resolved.price != null ? resolved.price : product.price,
      renammed: resolved.renammed,
      fromTender: resolved.fromTender,
    };
  }, [tenderCatalog, order?.deliveryInfo?.person, order?.prestationType]);

  /** Format item selon mode. */
  const buildLine = useCallback((product, qty) => {
    const { price, renammed } = tenderPriceFor(product);
    if (briqMode) {
      const localId = `auto_svc_${product.id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      return {
        _localId: localId,
        type: "product",
        sort: 0,
        name_override: null,
        text: null,
        product_id: product.id,
        effective_name: product.name,
        renammed: renammed,
        unit_price: price,
        quantity: qty,
      };
    }
    return {
      name: product.name,
      quantity: qty,
      price: price,
      product_id: product.id,
      classification_name: product.categoryName || null,
      type: "product",
      renammed: renammed,
    };
  }, [briqMode, tenderPriceFor]);

  /**
   * Sélectionne dans `serviceState.list` les 4 produits service nécessaires :
   * master/waiter × day/night. Le tag `isNight` (posé par
   * `app:tag-prestation-service` côté backend, slug `service-apres-23h`) sert
   * de critère pour distinguer jour vs nuit. Fallback : si aucun produit night
   * n'est trouvé, on retombe sur le produit day (le tarif ne sera pas majoré
   * mais le service ne disparaît pas).
   */
  const getServiceProducts = useCallback(() => {
    const list = serviceState.list || [];
    return {
      masterDay:   list.find((p) => p.role === "master" && !p.isNight) || null,
      masterNight: list.find((p) => p.role === "master" &&  p.isNight) || null,
      waiterDay:   list.find((p) => p.role === "waiter" && !p.isNight) || null,
      waiterNight: list.find((p) => p.role === "waiter" &&  p.isNight) || null,
    };
  }, [serviceState.list]);

  /** Localise les indices des lignes service dans la source courante. */
  const findServiceIndices = useCallback(() => {
    const products = getServiceProducts();
    const masterIds = [products.masterDay?.id, products.masterNight?.id].filter(Boolean);
    const waiterIds = [products.waiterDay?.id, products.waiterNight?.id].filter(Boolean);
    const items = briqMode
      ? (serviceBlockIndex !== -1 ? (order?.briqueDevis?.[serviceBlockIndex]?.items || []) : [])
      : (order?.items || []);
    const masterIdx = [];
    const waiterIdx = [];
    items.forEach((it, i) => {
      if (masterIds.includes(it.product_id)) masterIdx.push(i);
      if (waiterIds.includes(it.product_id)) waiterIdx.push(i);
    });
    return { products, masterIdx, waiterIdx, items };
  }, [briqMode, serviceBlockIndex, order, getServiceProducts]);

  /**
   * Construit la liste des lignes service à injecter pour un rôle :
   *  - 1 ligne avec produit `day` × `dayHours` (si dayHours > 0)
   *  - 1 ligne avec produit `night` × `nightHours` (si nightHours > 0)
   * Si pas de produit night dispo, les heures nuit sont fusionnées sur le day.
   *
   * Multiplie ensuite par `count` (nombre de masters / serveurs).
   */
  const buildRoleLines = useCallback((count, dayProduct, nightProduct, hours) => {
    if (!count || count <= 0) return [];
    const lines = [];
    const dayP = dayProduct;
    const nightP = nightProduct;
    if (!dayP && !nightP) return [];

    for (let i = 0; i < count; i++) {
      if (hours.day > 0) {
        const productForDay = dayP || nightP;
        lines.push(buildLine(productForDay, hours.day));
      }
      if (hours.night > 0) {
        if (nightP) {
          lines.push(buildLine(nightP, hours.night));
        } else {
          // Pas de produit night en catalogue → fusionner sur day.
          lines.push(buildLine(dayP, hours.night));
        }
      }
    }
    return lines;
  }, [buildLine]);

  /**
   * Crée N lignes service (atomique en briqMode, batché en classique).
   * À appeler depuis updateServiceFlag(true) dans le composant.
   */
  const createServiceLines = useCallback((masterCount, waiterCount) => {
    if (!order) return;
    const startAt = order.deliveryInfo?.startAt || null;
    const endAt = order.deliveryInfo?.endAt || null;
    const hours = calculateServiceHoursSplit(startAt, endAt);
    const products = getServiceProducts();
    if (!products.masterDay && !products.masterNight && !products.waiterDay && !products.waiterNight) return;

    const masterLines = buildRoleLines(masterCount, products.masterDay, products.masterNight, hours);
    const waiterLines = buildRoleLines(waiterCount, products.waiterDay, products.waiterNight, hours);
    const newLines = [...masterLines, ...waiterLines];
    if (newLines.length === 0) return;

    if (briqMode) {
      if (serviceBlockIndex === -1) return;
      const block = order.briqueDevis?.[serviceBlockIndex];
      if (!block) return;
      const existing = block.items || [];
      const items = [...existing, ...newLines].map((it, idx) => ({ ...it, sort: idx }));
      dispatch(setOrderBriqueDevis(orderIndex, (prev) =>
        prev.map((b, i) => i === serviceBlockIndex ? { ...b, items } : b)
      ));
    } else {
      newLines.forEach((line) => dispatch(addOrderItem(orderIndex, line)));
    }
  }, [order, briqMode, serviceBlockIndex, buildRoleLines, getServiceProducts, dispatch, orderIndex]);

  /** Supprime TOUTES les lignes service. À appeler depuis updateServiceFlag(false). */
  const removeAllServiceLines = useCallback(() => {
    if (!order) return;
    const serviceIds = serviceState.list.map((p) => p.id);
    if (!serviceIds.length) return;

    if (briqMode) {
      if (serviceBlockIndex === -1) return;
      const block = order.briqueDevis?.[serviceBlockIndex];
      if (!block) return;
      const filtered = (block.items || []).filter((it) => !serviceIds.includes(it.product_id));
      if (filtered.length === (block.items || []).length) return;
      dispatch(setOrderBriqueDevis(orderIndex, (prev) =>
        prev.map((b, i) => i === serviceBlockIndex
          ? { ...b, items: filtered.map((it, idx) => ({ ...it, sort: idx })) }
          : b
        )
      ));
    } else {
      const items = order.items || [];
      for (let i = items.length - 1; i >= 0; i--) {
        if (serviceIds.includes(items[i].product_id)) {
          dispatch(removeOrderItem(orderIndex, i));
        }
      }
    }
  }, [order, serviceState.list, briqMode, serviceBlockIndex, dispatch, orderIndex]);

  /**
   * Calcule le set complet de lignes service attendu (sans dispatch) en
   * fonction des counts (masterCount/waiterCount) et des horaires actuels
   * de la presta. Sert :
   *  - à `rebuildServiceLines()` (qui applique le résultat),
   *  - à `isServiceCalcOutOfSync()` pour décider d'afficher le bouton rouge
   *    "Actualiser le calcul".
   *
   * @param {number} masterCount
   * @param {number} waiterCount
   * @returns {{lines: Array, hours: {total:number, day:number, night:number}}}
   */
  const getExpectedServiceLines = useCallback((masterCount, waiterCount) => {
    const startAt = order?.deliveryInfo?.startAt || null;
    const endAt = order?.deliveryInfo?.endAt || null;
    const hours = calculateServiceHoursSplit(startAt, endAt);
    const products = getServiceProducts();
    const masterLines = buildRoleLines(masterCount, products.masterDay, products.masterNight, hours);
    const waiterLines = buildRoleLines(waiterCount, products.waiterDay, products.waiterNight, hours);
    return { lines: [...masterLines, ...waiterLines], hours };
  }, [order, getServiceProducts, buildRoleLines]);

  /**
   * Reconstruit l'ensemble des lignes service en remplacement de l'existant.
   * Unifie l'ancien `syncServiceLinesToCounts` (changement de counts) et
   * `syncServiceLinesQty` (changement d'heures) : un seul recompute complet
   * basé sur les counts + horaires actuels.
   *
   * À appeler depuis :
   *  - le `onChange` de masterCount/waiterCount/startAt/endAt,
   *  - le bouton rouge "Actualiser le calcul" quand `isServiceCalcOutOfSync`,
   *  - `createServiceLines()` à la coche initiale du toggle.
   */
  const rebuildServiceLines = useCallback((masterCount, waiterCount) => {
    if (!order) return;
    const { lines: newLines } = getExpectedServiceLines(masterCount, waiterCount);
    const products = getServiceProducts();
    const serviceProductIds = [
      products.masterDay?.id, products.masterNight?.id,
      products.waiterDay?.id, products.waiterNight?.id,
    ].filter(Boolean);
    if (serviceProductIds.length === 0) return;

    if (briqMode) {
      if (serviceBlockIndex === -1) return;
      const block = order.briqueDevis?.[serviceBlockIndex];
      if (!block) return;
      // Retire les lignes service existantes, conserve le reste, ajoute les nouvelles.
      const others = (block.items || []).filter((it) => !serviceProductIds.includes(it.product_id));
      const items = [...newLines, ...others].map((it, idx) => ({ ...it, sort: idx }));
      dispatch(setOrderBriqueDevis(orderIndex, (prev) =>
        prev.map((b, i) => i === serviceBlockIndex ? { ...b, items } : b)
      ));
    } else {
      // Mode classique : on retire en reverse + on ajoute.
      const items = order.items || [];
      for (let i = items.length - 1; i >= 0; i--) {
        if (serviceProductIds.includes(items[i].product_id)) {
          dispatch(removeOrderItem(orderIndex, i));
        }
      }
      newLines.forEach((line) => dispatch(addOrderItem(orderIndex, line)));
    }
  }, [order, briqMode, serviceBlockIndex, getExpectedServiceLines, getServiceProducts, dispatch, orderIndex]);

  /**
   * Vrai si l'ensemble des lignes service actuel ne reflète plus la formule
   * (counts × horaires actuels). Permet d'afficher le bouton rouge
   * "Actualiser le calcul" dans la brique service.
   *
   * Comparaison : on extrait `(product_id, quantity)` des lignes existantes
   * vs attendues, et on compare les multisets.
   */
  const isServiceCalcOutOfSync = useCallback((masterCount, waiterCount) => {
    if (!order) return false;
    const products = getServiceProducts();
    const serviceProductIds = [
      products.masterDay?.id, products.masterNight?.id,
      products.waiterDay?.id, products.waiterNight?.id,
    ].filter(Boolean);
    if (serviceProductIds.length === 0) return false;

    const items = briqMode
      ? (serviceBlockIndex !== -1 ? (order?.briqueDevis?.[serviceBlockIndex]?.items || []) : [])
      : (order?.items || []);
    const existing = items
      .filter((it) => serviceProductIds.includes(it.product_id))
      .map((it) => `${it.product_id}|${Number(it.quantity)}`)
      .sort();

    const { lines: expectedLines } = getExpectedServiceLines(masterCount, waiterCount);
    const expected = expectedLines
      .map((l) => `${l.product_id}|${Number(briqMode ? l.quantity : l.quantity)}`)
      .sort();

    if (existing.length !== expected.length) return true;
    for (let i = 0; i < existing.length; i++) {
      if (existing[i] !== expected[i]) return true;
    }
    return false;
  }, [order, briqMode, serviceBlockIndex, getServiceProducts, getExpectedServiceLines]);

  /**
   * Compare le prix unitaire des lignes service présentes au tarif de l'appel
   * d'offres sélectionné. Sert à signaler le cas « l'AO a été choisi (ou
   * changé) après la création des lignes service » : les prix posés sont alors
   * restés au tarif standard.
   *
   * Retourne toujours un tableau (vide hors AO ou si tout est aligné) de
   * `{ index, name, currentPrice, tenderPrice }`.
   */
  const getServiceTenderPriceGaps = useCallback(() => {
    if (!order || !tenderCatalog?.items?.length) return [];
    const { products, items } = findServiceIndices();
    const serviceProducts = [products.masterDay, products.masterNight, products.waiterDay, products.waiterNight]
      .filter(Boolean);
    if (!serviceProducts.length) return [];

    const gaps = [];
    items.forEach((it, index) => {
      const product = serviceProducts.find((p) => p.id === it.product_id);
      if (!product) return;
      const { price: tenderPrice, fromTender } = tenderPriceFor(product);
      if (!fromTender) return;
      const currentPrice = Number(it.unit_price ?? it.price ?? 0);
      if (currentPrice !== Number(tenderPrice)) {
        gaps.push({
          index,
          name: it.effective_name || it.name || product.name,
          currentPrice,
          tenderPrice: Number(tenderPrice),
        });
      }
    });
    return gaps;
  }, [order, tenderCatalog, findServiceIndices, tenderPriceFor]);

  /**
   * Réaligne le prix unitaire des lignes service sur le tarif AO, sans toucher
   * aux quantités ni au reste du devis (1 dispatch en mode brique).
   */
  const applyTenderPricesToServiceLines = useCallback(() => {
    const gaps = getServiceTenderPriceGaps();
    if (!gaps.length) return 0;
    const priceByIndex = new Map(gaps.map((g) => [g.index, g.tenderPrice]));

    if (briqMode) {
      if (serviceBlockIndex === -1) return 0;
      dispatch(setOrderBriqueDevis(orderIndex, (prev) =>
        prev.map((b, i) => i === serviceBlockIndex
          ? {
            ...b,
            items: (b.items || []).map((it, j) =>
              priceByIndex.has(j) ? { ...it, unit_price: priceByIndex.get(j) } : it
            ),
          }
          : b
        )
      ));
    } else {
      const items = order?.items || [];
      priceByIndex.forEach((price, index) => {
        const current = items[index];
        if (!current) return;
        dispatch(updateOrderItem(orderIndex, index, { ...current, price }));
      });
    }
    return gaps.length;
  }, [getServiceTenderPriceGaps, briqMode, serviceBlockIndex, order, dispatch, orderIndex]);

  /** Update d'un item service (qty ou prix) — 1 dispatch. */
  const updateServiceLine = useCallback((sourceIdx, patch) => {
    if (!order) return;
    if (briqMode) {
      if (serviceBlockIndex === -1) return;
      dispatch(setOrderBriqueDevis(orderIndex, (prev) =>
        prev.map((b, i) => i === serviceBlockIndex
          ? { ...b, items: (b.items || []).map((it, j) => j === sourceIdx ? { ...it, ...patch } : it) }
          : b
        )
      ));
    } else {
      const current = (order.items || [])[sourceIdx];
      if (!current) return;
      dispatch(updateOrderItem(orderIndex, sourceIdx, { ...current, ...patch }));
    }
  }, [order, briqMode, serviceBlockIndex, dispatch, orderIndex]);

  /** Supprime une ligne service précise — 1 dispatch. */
  const removeServiceLine = useCallback((sourceIdx) => {
    if (!order) return;
    if (briqMode) {
      if (serviceBlockIndex === -1) return;
      dispatch(setOrderBriqueDevis(orderIndex, (prev) =>
        prev.map((b, i) => i === serviceBlockIndex
          ? { ...b, items: (b.items || []).filter((_, j) => j !== sourceIdx).map((it, idx) => ({ ...it, sort: idx })) }
          : b
        )
      ));
    } else {
      dispatch(removeOrderItem(orderIndex, sourceIdx));
    }
  }, [order, briqMode, serviceBlockIndex, dispatch, orderIndex]);

  return {
    briqMode,
    serviceBlockIndex,
    // Création / suppression
    createServiceLines,
    removeAllServiceLines,
    // Recompute unifié (counts + horaires) — remplace `syncServiceLinesToCounts` + `syncServiceLinesQty`
    rebuildServiceLines,
    // Aliases rétrocompatibilité
    syncServiceLinesToCounts: rebuildServiceLines,
    syncServiceLinesQty: () => {
      const m = toInt(order?.serviceInfo?.masterCount, 1);
      const w = toInt(order?.serviceInfo?.waiterCount, 0);
      rebuildServiceLines(m, w);
    },
    // Détection désync (pour bouton rouge "Actualiser")
    isServiceCalcOutOfSync,
    getExpectedServiceLines,
    // Tarifs appel d'offres sur les lignes service
    getServiceTenderPriceGaps,
    applyTenderPricesToServiceLines,
    // Édition manuelle ligne par ligne
    updateServiceLine,
    removeServiceLine,
    findServiceIndices,
    // Validation cross-brique (à afficher en bannière bloquante)
    findServiceOptionConflicts,
  };
};

export default useServiceLineSync;
